import * as Reducers from 'src/app/store/reducers';
import * as SettingsActions from '../../store/settings/actions';
import {BehaviorSubject, Observable} from 'rxjs';
import { DeveloperHardwareConfig } from '../../store/settings/DeveloperHardwareConfig';
import { environment } from '../../../environments/environment';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';

@Injectable({
	providedIn: 'root'
})
export class DeveloperService {
	public readonly hardwareConfig$: BehaviorSubject<DeveloperHardwareConfig>;
	private _isDevMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
		!environment.production
	);
	private _isDemoMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
		!environment.production || !!environment.demo
	);
	private _hasDemoToggleMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
		!environment.production || !!environment.demo
	);

	private hardwareConfig: DeveloperHardwareConfig = {
		overrideHardwareStatus: false,
		emulateTouchScreen: false,
		emulateButtonDevice: false,
		emulateTappingPen: false,
		emulateSoundDevice: false,
		emulateExternalAudiometry: false,
		emulateAudiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB: false,
		emulateAudiometry_bd_DT_770_PRO_32__Sb_X_G1: false,
		emulateExternalBicycleErgometer: false,
		emulateTestHardware: false,
	};

	constructor(
		private store: Store<any>,
	) {
		this.hardwareConfig$ = new BehaviorSubject(this.hardwareConfig);

		if (!DeveloperService.isDevModePossible)
			return;

		this.initFromStore();
		this.printProductiveWarning();
	}

	private static isDevModePossible(): boolean {
		return !environment.production || !!environment.demo;
	}

	private initFromStore(): void {
		this.store.pipe(select(Reducers.getDeveloperSettings)).subscribe(state => {
			this.parseHardwareConfig(state);
			this.pushNewHardwareConfig();
		});
	}

	set isDevMode(devMode: boolean) {
		this._isDevMode$.next(devMode);
		this.isDemoMode = devMode;
	}

	set isDemoMode(demoMode: boolean) {
		this._isDemoMode$.next(demoMode);
	}

	/**
	 * DevModus entspricht der absoluten Entwicklungsumgebung. Der Status kann nur verändert werden,
	 * wenn kein 'production' in der environment-Variablen vorliegt.
	 */
	get isDevMode$(): Observable<boolean> {
		return this._isDevMode$;
	}

	/**
	 * DemoModus kann bei 'production' in der environment-Variablen nur dann getoggelt werden,
	 * wenn 'allowDevToggling' gesetzt ist.
	 */
	get isDemoMode$(): Observable<boolean> {
		return this._isDemoMode$;
	}

	get hasDemoToggleMode$(): Observable<boolean> {
		return this._hasDemoToggleMode$;
	}

	private parseHardwareConfig(config: DeveloperHardwareConfig): void {
		this.hardwareConfig = {
			overrideHardwareStatus: config.overrideHardwareStatus,
			emulateTouchScreen: config.emulateTouchScreen,
			emulateButtonDevice: config.emulateButtonDevice,
			emulateTappingPen: config.emulateTappingPen,
			emulateSoundDevice: config.emulateSoundDevice,
			emulateExternalAudiometry: config.emulateExternalAudiometry,
			emulateAudiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB: config.emulateAudiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB,
			emulateAudiometry_bd_DT_770_PRO_32__Sb_X_G1: config.emulateAudiometry_bd_DT_770_PRO_32__Sb_X_G1,
			emulateExternalBicycleErgometer: config.emulateExternalBicycleErgometer,
			emulateTestHardware: config.emulateTestHardware,
		};
	}

	private pushNewHardwareConfig(): void {
		this.hardwareConfig$.next(this.hardwareConfig);
	}

	public changeSettings(config: DeveloperHardwareConfig): void {
		this.store.dispatch(
			new SettingsActions.SetDevStateAction(config),
		);
	}

	private printProductiveWarning(): void {
		console.warn(
			'                         __    _                              \n' +
			'                    _wr""        "-q__                             \n' +
			'                 _dP                 9m_     \n' +
			'               _#P                     9#_                         \n' +
			'              d#@                       9#m                        \n' +
			'             d##                         ###                       \n' +
			'            J###                         ###L                      \n' +
			'            {###K                       J###K                      \n' +
			'            ]####K      ___aaa___      J####F                      \n' +
			'        __gmM######_  w#P""   ""9#m  _d#####Mmw__                  \n' +
			'     _g##############mZ_         __g##############m_               \n' +
			'   _d####M@PPPP@@M#######Mmp gm#########@@PPP9@M####m_             \n' +
			'  a###""          ,Z"#####@" \'######"\\g          ""M##m          \n' +
			' J#@"             0L  "*##     ##@"  J#              *#K           \n' +
			' #"               `#    "_gmwgm_~    dF               `#_          \n' +
			'7F                 "#_   ]#####F   _dK                 JE          \n' +
			']                    *m__ ##### __g@"                   F          \n' +
			'                       "PJ#####LP"                                 \n' +
			' `                       0######_                      \'          \n' +
			'                       _0########_                                 \n' +
			'     .               _d#####^#####m__              ,               \n' +
			'      "*w_________am#####P"   ~9#####mw_________w*"                \n' +
			'          ""9@#####@M""           ""P@#####@M""                    \n' +
			'\n' +
			'Anwendung befindet sich im DEMO-Modus. Finale QA muss zwingend \n' +
			'auf einer Produktivversion stattfinden!\n', environment , this.hardwareConfig
		);
	}
}
